/**
 * Redirecionadores — o link curto que distribui o lead entre os grupos com vaga.
 *
 * Quatro decisões que valem o comentário:
 *
 *  - **O diagnóstico de origem vem ANTES da lista.** É o maior valor da tela inteira. O
 *    desenho de produto é "o cliente põe o nosso link no botão da LP dele", e a armadilha é
 *    que os `utm_*` e o `fbclid` ficam na URL da LP, não no `href` do botão: com um link
 *    estático o redirect funciona, a pessoa entra no grupo, e a atribuição morre em silêncio
 *    (ADR-0013). Um aviso enterrado no rodapé não evitaria o cliente culpar o anúncio.
 *  - **O endereço aparece e não é editável.** Mesma regra do código da campanha: ele já pode
 *    estar dentro de um anúncio no ar. O que a tela oferece é COPIAR, não reescrever.
 *  - **Pausar, arquivar e excluir são três coisas.** Pausar para de distribuir e o endereço
 *    continua de pé; arquivar tira da operação; excluir é terminal e só aparece no link já
 *    arquivado — a mesma ordem que o servidor exige.
 *  - **Nada de `confirm()`.** Ação destrutiva usa diálogo próprio com o eco do que vai
 *    acontecer; em fluxo repetido, `confirm` vira tapinha automático.
 *
 * No celular a tabela vira lista de cartões, e toda ação importante é botão de largura cheia
 * (DESIGN_SYSTEM §11).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  TableSkeleton,
  TableEntity,
  useToast,
} from '@mgdlead/ui';
import type { ActionMenuItem } from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Link2,
  MousePointerClick,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Shuffle,
  Trash2,
} from 'lucide-react';

import { FormAlert } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { GuiaDeConexao } from '../../../features/guias/GuiaDeConexao';
import { GUIA_REDIRECIONADOR } from '../../../features/guias/conexoes';
import { AllowlistDrawer } from './AllowlistDrawer';
import { OriginDiagnosisPanel } from './OriginDiagnosisPanel';
import { RedirectorDrawer } from './RedirectorDrawer';
import { RedirectorFormDialog } from './RedirectorFormDialog';
import {
  DIAGNOSIS,
  READINESS,
  REDIRECTOR_STRATEGY,
  avaliarEntrega,
  diagnoseOrigin,
  efeitoDeNenhumPronto,
  precisaDeConserto,
  redirectorState,
  resumoDaEntrega,
  enderecoPublico,
} from '../../../features/redirector/redirector-ui';
import type { DeliveryReadiness } from '../../../features/redirector/redirector-ui';
import { inviteRefreshSummary } from '../../../features/group/group-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import {
  useAddAllowlistUrl,
  useArchiveRedirector,
  useCampaignGroupsMany,
  useCampaigns,
  useCreateRedirector,
  useGroups,
  useRedirectorAllowlist,
  useRedirectors,
  useRefreshGroupInvite,
  useRemoveAllowlistUrl,
  useRemoveRedirector,
  useUnarchiveRedirector,
  useUpdateRedirector,
} from '../../../lib/api';
import type { Redirector, RedirectorLimit } from '../../../lib/api';
import { formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { useOpenFromParam } from '../../../features/experience/useOpenFromParam';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/**
 * A frase que ensina o termo. Uma só, usada em dois lugares.
 *
 * Ela dizia "cada clique vai para o próximo grupo da campanha que ainda tem vaga" — a
 * descrição do `FILL_SEQUENTIAL` escrita como se fosse a regra de todo link. Com três
 * estratégias isso passou a ser falso em dois terços dos casos, e quem explica o
 * comportamento de CADA link é a estratégia dele (ver `REDIRECTOR_STRATEGY`), não este texto.
 */
const O_QUE_E =
  'Um redirecionador é o link curto que você põe no botão “Entrar no grupo” da sua página. Você escolhe como ele reparte os cliques entre os grupos da campanha.';

type Confirm =
  { kind: 'archive'; redirector: Redirector } | { kind: 'remove'; redirector: Redirector };

export function RedirectorsPage() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  useDocumentTitle('Redirecionadores');

  const [includeArchived, setIncludeArchived] = useState(false);
  const redirectorsQuery = useRedirectors(workspace.id, includeArchived);
  const campaignsQuery = useCampaigns(workspace.id);
  const allowlistQuery = useRedirectorAllowlist(workspace.id);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Redirector | null>(null);
  const [detail, setDetail] = useState<Redirector | null>(null);
  const [allowlistOpen, setAllowlistOpen] = useState(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const create = useCreateRedirector();
  const update = useUpdateRedirector();
  const archive = useArchiveRedirector();
  const unarchive = useUnarchiveRedirector();
  const remove = useRemoveRedirector();
  const addAllowlist = useAddAllowlistUrl();
  const removeAllowlist = useRemoveAllowlistUrl();

  const canCreate = can(role, 'redirector:create');
  const canUpdate = can(role, 'redirector:update');
  const canDelete = can(role, 'redirector:delete');
  /*
   * Rebuscar o convite escreve numa coluna do GRUPO, então a permissão é a do grupo
   * (`group:update`) — a mesma que o servidor cobra. Esconder por `can()` é UX: o que se
   * evita é oferecer um botão que só daria 403.
   */
  const canUpdateGroup = can(role, 'group:update');
  const canEditPage = can(role, 'redirector:page');

  /** `pii:reveal` (OWNER/ADMIN): revelar o telefone na aba Quem clicou. O servidor cobra igual. */
  const canRevealPhone = can(role, 'pii:reveal');

  const data = redirectorsQuery.data;
  const redirectors = useMemo(() => data?.redirectors ?? [], [data]);
  const limit: RedirectorLimit | undefined = data?.limit;
  /* O servidor já respondeu "cabe mais um?". Aqui só se LÊ a resposta (invariante 4). */
  const limitReached = limit !== undefined && !limit.unlimited && !limit.allowed;

  /**
   * Os links que estão recebendo clique sem origem.
   *
   * É um FILTRO da lista que o servidor mandou, não uma métrica: nenhum número é somado,
   * dividido ou reescrito aqui — cada linha mostra os dois contadores como vieram do banco
   * (invariante 3).
   */
  const comProblema = useMemo(
    () => redirectors.filter((r) => precisaDeConserto(diagnoseOrigin(r))),
    [redirectors],
  );

  const campaigns = useMemo(
    () => (campaignsQuery.data?.campaigns ?? []).filter((c) => c.archivedAt === null),
    [campaignsQuery.data],
  );
  const allowlist = useMemo(() => allowlistQuery.data ?? [], [allowlistQuery.data]);

  /* ---------------------------------------------------------------------- */
  /*  Prontidão de entrega: quais grupos deste link recebem gente de verdade  */
  /* ---------------------------------------------------------------------- */

  /**
   * **Duas consultas para uma pergunta, porque nenhuma das duas a responde sozinha.**
   *
   * `campaign.groups` sabe QUAIS grupos o link alcança e em que estado eles estão; `group.list`
   * é a única que traz o `inviteCode` — e o convite é o que decide se o grupo recebe alguém
   * (`invite_code is not null` na cláusula de elegibilidade do allocator). Juntar as duas aqui
   * é o que permite a tela dizer "2 de 3 grupos prontos" sem inventar nada.
   */
  const groupsQuery = useGroups(workspace.id);
  const campaignIds = useMemo(
    () => [...new Set(redirectors.map((r) => r.campaignId))],
    [redirectors],
  );
  const gruposPorCampanha = useCampaignGroupsMany(workspace.id, campaignIds);

  /**
   * O convite de um grupo, ou `undefined` para **não sabemos** (invariante 8): enquanto a
   * lista não chegou, a tela não pode acusar falta de convite — alarme falso aqui ensina a
   * ignorar o alarme verdadeiro.
   */
  const conviteDe = useCallback(
    (groupId: string): string | null | undefined => {
      // `== null` e não `=== undefined`: "ainda não veio" e "veio vazio" são a mesma
      // ignorância aqui, e as duas têm de virar `undefined` — nunca "sem convite".
      if (groupsQuery.data == null) return undefined;
      const grupo = groupsQuery.data.groups.find((g) => g.id === groupId);
      return grupo === undefined ? undefined : grupo.inviteCode;
    },
    [groupsQuery.data],
  );

  /** `null` = ainda não dá para dizer nada sobre este link. */
  const entregaPorLink = useMemo(() => {
    const mapa = new Map<string, DeliveryReadiness | null>();
    for (const redirector of redirectors) {
      const grupos = gruposPorCampanha.porCampanha.get(redirector.campaignId);
      mapa.set(redirector.id, grupos == null ? null : avaliarEntrega(grupos, conviteDe));
    }
    return mapa;
  }, [conviteDe, gruposPorCampanha, redirectors]);

  /**
   * Os links que hoje mandariam gente para lugar nenhum por falta de convite.
   *
   * É o caso que originou tudo: um redirecionador criado hoje mandaria 100% dos cliques para
   * o destino de reserva porque nenhum grupo tinha convite — e nada na tela dizia isso. Só
   * entram links VIVOS: acusar um link arquivado seria barulho sobre coisa que já saiu do ar.
   */
  const semConvite = useMemo(
    () =>
      redirectors.filter((redirector) => {
        if (redirector.archivedAt !== null) return false;
        const entrega = entregaPorLink.get(redirector.id);
        return entrega != null && entrega.semConvite > 0;
      }),
    [entregaPorLink, redirectors],
  );

  const refreshInvite = useRefreshGroupInvite();
  const buscarConvite = useCallback(
    (groupId: string) => {
      setRowError(null);
      refreshInvite.mutate(
        { workspaceId: workspace.id, groupId },
        {
          onSuccess: (resultado) => {
            // `changed: false` é BOA notícia, e o toast diz isso — ver `inviteRefreshSummary`.
            const resumo = inviteRefreshSummary(resultado);
            toast({ title: resumo.title, description: resumo.description, tone: resumo.tone });
          },
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
    [refreshInvite, toast, workspace.id],
  );

  const openCreate = useCallback(() => {
    setRowError(null);
    setEditing(null);
    setFormOpen(true);
  }, []);
  // "Novo …" da busca (⌘K) chega como `?novo=1`.
  useOpenFromParam('novo', openCreate, canCreate);

  const submitForm = useCallback(
    async (values: {
      name: string;
      campaignId: string;
      slug: string;
      fallbackUrlId: string;
      strategy: Redirector['strategy'];
      pinnedGroupId: string;
      noGroupAction: Redirector['noGroupAction'];
      contingencyGroupId: string;
    }) => {
      // `""` no select é "sem destino de reserva"; o servidor espera `null` para remover e
      // um id para apontar. São coisas diferentes, e o `??` mal colocado aqui apagaria o
      // fallback de quem só renomeou o link.
      const fallbackUrlId = values.fallbackUrlId.length === 0 ? null : values.fallbackUrlId;
      /*
       * `pinnedGroupId` só viaja na estratégia que o usa. Nas outras ele é OMITIDO, e não
       * mandado como `null`: o servidor limpa o grupo sozinho quando a estratégia sai de
       * `SPECIFIC_GROUP` (ver `updateRedirector`), enquanto mandar um grupo junto de uma
       * estratégia que não o usa é contradição — e a rota recusa, com razão.
       */
      const pinned =
        values.strategy === 'SPECIFIC_GROUP' ? { pinnedGroupId: values.pinnedGroupId } : {};
      // B6: o mesmo raciocínio — o grupo de contingência só viaja com a opção que o usa.
      const semGrupo = {
        noGroupAction: values.noGroupAction,
        ...(values.noGroupAction === 'CONTINGENCY_GROUP'
          ? { contingencyGroupId: values.contingencyGroupId }
          : {}),
      };

      if (editing) {
        await update.mutateAsync({
          workspaceId: workspace.id,
          redirectorId: editing.id,
          name: values.name,
          campaignId: values.campaignId,
          fallbackUrlId,
          strategy: values.strategy,
          ...pinned,
          ...semGrupo,
        });
        toast({ title: 'Link atualizado', tone: 'success' });
        return;
      }

      const criado = await create.mutateAsync({
        workspaceId: workspace.id,
        name: values.name,
        campaignId: values.campaignId,
        ...(values.slug.length === 0 ? {} : { slug: values.slug }),
        fallbackUrlId,
        strategy: values.strategy,
        ...pinned,
        ...semGrupo,
      });
      toast({
        title: `Link criado — /r/${criado.slug}`,
        description: 'Copie o endereço e o trecho de instalação para o botão da sua página.',
        tone: 'success',
      });
      // Emenda direto no passo seguinte: link criado e não instalado não vale nada.
      setDetail(criado);
    },
    [create, editing, toast, update, workspace.id],
  );

  const togglePause = useCallback(
    (redirector: Redirector) => {
      setRowError(null);
      update.mutate(
        { workspaceId: workspace.id, redirectorId: redirector.id, isActive: !redirector.isActive },
        {
          onSuccess: (updated) =>
            toast({
              title: updated.isActive ? 'Link no ar' : 'Link pausado',
              description: updated.isActive
                ? 'Volta a mandar gente para os grupos da campanha.'
                : 'Para de mandar gente para os grupos. O endereço continua de pé e o clique continua sendo registrado.',
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
    [toast, update, workspace.id],
  );

  const rowProps = {
    canUpdate,
    canDelete,
    onDetail: (redirector: Redirector) => {
      setRowError(null);
      setDetail(redirector);
    },
    onEdit: (redirector: Redirector) => {
      setRowError(null);
      setEditing(redirector);
      setFormOpen(true);
    },
    onTogglePause: togglePause,
    onArchive: (redirector: Redirector) => setConfirm({ kind: 'archive', redirector }),
    onRemove: (redirector: Redirector) => setConfirm({ kind: 'remove', redirector }),
    onUnarchive: (redirector: Redirector) => {
      setRowError(null);
      unarchive.mutate(
        { workspaceId: workspace.id, redirectorId: redirector.id },
        {
          onSuccess: () =>
            toast({
              title: 'Link desarquivado',
              description: 'Voltou pausado — religue quando quiser que ele distribua de novo.',
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
  };

  return (
    <>
      <PageHeader
        title="Redirecionadores"
        description={O_QUE_E}
        action={
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus />}
              leadingBubble
              block
              disabled={limitReached}
              onClick={openCreate}
            >
              Novo redirecionador
            </Button>
          ) : null
        }
      />

      {/*
        O CONVITE VEM ANTES ATÉ DO DIAGNÓSTICO DE ORIGEM, e a ordem é a do estrago: sem
        origem o clique funciona e só a atribuição se perde; sem convite o grupo não recebe
        NINGUÉM, e o link inteiro vira desperdício de anúncio.
      */}
      {semConvite.length > 0 ? (
        <AvisoDeConvite
          afetados={semConvite}
          entregaPorLink={entregaPorLink}
          podeBuscar={canUpdateGroup}
          buscando={refreshInvite.isPending}
          onBuscar={buscarConvite}
        />
      ) : null}

      {/*
        O DIAGNÓSTICO VEM PRIMEIRO. Ver a seção de cabeçalho deste arquivo: é o que evita o
        cliente desligar um anúncio que estava funcionando por causa de um botão mal
        instalado na página dele.
      */}
      {comProblema.length > 0 ? (
        <AvisoDeOrigem afetados={comProblema} onAbrir={(redirector) => setDetail(redirector)} />
      ) : null}

      {limitReached && limit !== undefined ? (
        <FormAlert tone="info" className="mb-3">
          Seu plano permite {limit.limit} {limit.limit === 1 ? 'link' : 'links'} e você já está com{' '}
          {limit.current}. Arquive um link ou mude de plano para criar outro.
        </FormAlert>
      ) : null}

      {rowError !== null ? <FormAlert className="mb-3">{rowError}</FormAlert> : null}

      <Card padded={false}>
        <div className="flex flex-col gap-3 px-4 pt-4 md:flex-row md:items-start md:justify-between md:px-5 md:pt-5">
          <CardHeader
            className="mb-0"
            title="Seus links"
            description="O endereço de cada link é fixo para sempre: ele pode estar dentro de um anúncio no ar, e trocá-lo faria esse anúncio apontar para o nada."
          />

          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <Button
              size="sm"
              leadingIcon={<Link2 />}
              onClick={() => setAllowlistOpen(true)}
              className="max-md:w-full"
            >
              Destinos de reserva
            </Button>
            {(data?.archivedCount ?? 0) > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIncludeArchived((atual) => !atual)}
                className="max-md:w-full"
              >
                {includeArchived
                  ? 'Ocultar arquivados'
                  : `Mostrar arquivados (${data?.archivedCount ?? 0})`}
              </Button>
            ) : null}
          </div>
        </div>

        {redirectorsQuery.isPending ? (
          <div className="mt-4">
            <TableSkeleton rows={3} columns={5} />
          </div>
        ) : redirectorsQuery.isError ? (
          <ErrorState
            title="Não conseguimos carregar os redirecionadores"
            description={humanizeError(redirectorsQuery.error)}
            onRetry={() => void redirectorsQuery.refetch()}
          />
        ) : redirectors.length === 0 ? (
          <EmptyState
            kind="zero"
            icon={Shuffle}
            title="Nenhum redirecionador ainda"
            description={
              <>
                {O_QUE_E} Crie o primeiro e nós entregamos o endereço pronto para copiar, junto com
                o trecho que faz a sua página repassar de qual anúncio a pessoa veio.
              </>
            }
            action={
              canCreate ? (
                <Button
                  variant="primary"
                  leadingIcon={<Plus />}
                  disabled={limitReached}
                  onClick={openCreate}
                  className="max-md:w-full"
                >
                  Criar o primeiro link
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            {/* ---------------------------- celular ---------------------------- */}
            <ul className="mt-3 flex flex-col divide-y divide-border border-t border-border md:hidden">
              {redirectors.map((redirector) => (
                <li key={redirector.id}>
                  <RedirectorCard
                    redirector={redirector}
                    entrega={entregaPorLink.get(redirector.id) ?? null}
                    {...rowProps}
                  />
                </li>
              ))}
            </ul>

            {/* ---------------------------- desktop ---------------------------- */}
            <div className="mt-4 max-md:hidden">
              <Table caption={`Redirecionadores de ${workspace.name}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Link</TableHead>
                    <TableHead>Campanha</TableHead>
                    <TableHead numeric>Cliques</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead sticky="right">
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {redirectors.map((redirector) => (
                    <RedirectorRow
                      key={redirector.id}
                      redirector={redirector}
                      entrega={entregaPorLink.get(redirector.id) ?? null}
                      {...rowProps}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Card>

      {/*
        O guia de instalação. Sem nenhum link criado ele vem ABERTO; com link criado encolhe
        para uma linha e fica. O motivo de não sumir está escrito no próprio guia: "colei só
        o endereço em vez do trecho inteiro" é um erro que NÃO dá erro — os cliques chegam,
        as pessoas entram, e a atribuição morre calada. Quem vem investigar isso volta para
        esta tela, não para um curso noutra aba.
      */}
      {redirectorsQuery.isSuccess ? (
        <GuiaDeConexao
          guia={GUIA_REDIRECIONADOR}
          recolhido={redirectors.length > 0}
          className="mt-4"
        />
      ) : null}

      {/* ------------------------------ diálogos ------------------------------ */}

      <RedirectorFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        mode={editing ? 'edit' : 'create'}
        workspaceId={workspace.id}
        campaigns={campaigns}
        allowlist={allowlist}
        initialName={editing?.name ?? ''}
        initialCampaignId={editing?.campaignId ?? ''}
        initialFallbackUrlId={editing?.fallbackUrlId ?? ''}
        initialStrategy={editing?.strategy ?? 'FILL_SEQUENTIAL'}
        initialPinnedGroupId={editing?.pinnedGroupId ?? ''}
        initialNoGroupAction={editing?.noGroupAction ?? 'FALLBACK_URL'}
        initialContingencyGroupId={editing?.contingencyGroupId ?? ''}
        onSubmit={submitForm}
      />

      <RedirectorDrawer
        workspaceId={workspace.id}
        timezone={workspace.timezone}
        redirector={detail}
        canRefreshInvite={canUpdateGroup}
        canTestLink={canUpdate}
        canEditPage={canEditPage}

        canRevealPhone={canRevealPhone}
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      />

      <AllowlistDrawer
        open={allowlistOpen}
        onOpenChange={setAllowlistOpen}
        urls={allowlist}
        canUpdate={canUpdate}
        removing={removeAllowlist.isPending}
        onAdd={async (values) => {
          await addAllowlist.mutateAsync({ workspaceId: workspace.id, ...values });
          toast({ title: 'Destino autorizado', tone: 'success' });
        }}
        onRemove={(allowlistUrlId) => {
          removeAllowlist.mutate(
            { workspaceId: workspace.id, allowlistUrlId },
            {
              onSuccess: () => toast({ title: 'Destino removido', tone: 'success' }),
              onError: (error: unknown) => setRowError(humanizeError(error)),
            },
          );
        }}
      />

      <Dialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
        title={confirm?.kind === 'remove' ? 'Excluir redirecionador' : 'Arquivar redirecionador'}
        description={
          confirm === null
            ? undefined
            : confirm.kind === 'remove'
              ? `“${confirm.redirector.name}” sai do painel de vez. Esta ação não tem como desfazer.`
              : `“${confirm.redirector.name}” sai da operação e para de mandar gente para os grupos.`
        }
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirm(null)}
              disabled={archive.isPending || remove.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={archive.isPending || remove.isPending}
              loadingLabel={confirm?.kind === 'remove' ? 'Excluindo' : 'Arquivando'}
              onClick={() => {
                if (confirm === null) return;
                const alvo = confirm.redirector;
                const mutation = confirm.kind === 'remove' ? remove : archive;
                mutation.mutate(
                  { workspaceId: workspace.id, redirectorId: alvo.id },
                  {
                    onSuccess: () => {
                      setConfirm(null);
                      toast({
                        title: confirm.kind === 'remove' ? 'Link excluído' : 'Link arquivado',
                        tone: 'success',
                      });
                    },
                    onError: (error: unknown) => {
                      setConfirm(null);
                      setRowError(humanizeError(error));
                    },
                  },
                );
              }}
            >
              {confirm?.kind === 'remove' ? 'Excluir' : 'Arquivar'}
            </Button>
          </>
        }
      >
        {/*
          O eco do que vai acontecer. A pergunta real de quem está prestes a clicar é "o
          endereço para de funcionar?" — e a resposta, nos dois casos, é que ele NÃO volta a
          distribuir, nem depois.
        */}
        <p className="text-sm text-muted-foreground">
          {confirm?.kind === 'remove' ? (
            <>
              O endereço <span className="font-mono">/r/{confirm.redirector.slug}</span> nunca mais
              será usado por nenhum link — nem por este, nem por outro. Os cliques já registrados
              continuam guardados.
            </>
          ) : confirm === null ? null : (
            <>
              Quem clicar em <span className="font-mono">/r/{confirm.redirector.slug}</span> deixa
              de entrar em grupo, mas o clique continua sendo registrado. Dá para desarquivar depois
              — e ele volta pausado.
            </>
          )}
        </p>
      </Dialog>
    </>
  );
}

/* ========================================================================== */
/*  Aviso de convite — o que faz o link não entregar NADA                      */
/* ========================================================================== */

/**
 * **O aviso que não existia, e que custou um link inteiro.**
 *
 * Um redirecionador criado hoje mandaria 100% dos cliques para o destino de reserva porque
 * nenhum dos grupos tinha convite — e a tela não dizia uma palavra: os grupos apareciam
 * ativos, com vaga, em dia. O allocator pula grupo sem `invite_code` **em silêncio**.
 *
 * Três decisões deste bloco:
 *
 *  - **nomeia o grupo, não só o link.** "Algo está errado" empurra o problema para o
 *    suporte; o que resolve é dizer qual grupo está sem convite.
 *  - **a ação está aqui, no lugar onde o problema é descoberto.** Mandar a pessoa para a
 *    tela do grupo para clicar num botão que ela ainda não sabe que existe é o mesmo tipo de
 *    caminho que não existe. O botão da tela do grupo continua lá, para quem chega por lá.
 *  - **um único botão por grupo, `secondary`.** A cor de ação da tela já está gasta no "Novo
 *    redirecionador" (DESIGN_SYSTEM §1.5), e um botão certo no meio de cinco iguais é um
 *    botão que ninguém acha.
 */
function AvisoDeConvite({
  afetados,
  entregaPorLink,
  podeBuscar,
  buscando,
  onBuscar,
}: {
  afetados: readonly Redirector[];
  entregaPorLink: Map<string, DeliveryReadiness | null>;
  podeBuscar: boolean;
  buscando: boolean;
  onBuscar: (groupId: string) => void;
}) {
  return (
    <section
      className="mb-4 flex flex-col gap-3 rounded-lg bg-surface p-4 ring-1 ring-inset ring-warning/40 md:p-5"
      aria-label="Aviso de convite"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-md font-medium text-warning">
          {afetados.length === 1
            ? 'Um link tem grupo que nunca vai receber ninguém'
            : `${afetados.length} links têm grupo que nunca vai receber ninguém`}
        </h2>
        <p className="text-sm text-muted-foreground">{READINESS.SEM_CONVITE.efeito}</p>
      </div>

      <ul className="flex flex-col divide-y divide-border">
        {afetados.map((redirector) => {
          const entrega = entregaPorLink.get(redirector.id);
          if (entrega == null) return null;
          return (
            <li key={redirector.id} className="flex flex-col gap-2 py-2">
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium">{redirector.name}</span>
                <span className="text-2xs text-muted-foreground">
                  {resumoDaEntrega(entrega)}
                  {entrega.nenhumPronto
                    ? ` ${efeitoDeNenhumPronto(redirector.fallbackUrl !== null)}`
                    : ''}
                </span>
              </span>

              <ul className="flex flex-col gap-2">
                {entrega.impedidos
                  .filter((linha) => linha.kind === 'SEM_CONVITE')
                  .map((linha) => (
                    <li
                      key={linha.groupId}
                      className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between"
                    >
                      <span className="flex items-center gap-1.5 text-sm">
                        <AlertTriangle
                          aria-hidden="true"
                          className="size-4 shrink-0 text-warning"
                        />
                        {linha.name ?? 'Sem nome no WhatsApp'}
                      </span>
                      {podeBuscar ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<RefreshCw />}
                          loading={buscando}
                          loadingLabel="Buscando"
                          aria-label={`Buscar convite de ${linha.name ?? 'grupo sem nome'}`}
                          onClick={() => onBuscar(linha.groupId)}
                          className="max-md:w-full"
                        >
                          Buscar convite
                        </Button>
                      ) : null}
                    </li>
                  ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ========================================================================== */
/*  Aviso de origem — o topo da tela                                           */
/* ========================================================================== */

/**
 * O aviso que abre a tela quando existe clique chegando sem origem.
 *
 * Ele nomeia os links afetados e mostra, por link, os DOIS números que o servidor contou —
 * sem porcentagem e sem soma (invariante 3). O botão leva direto para onde está o conserto,
 * porque um aviso que só diz "tem algo errado" empurra o problema para o suporte.
 */
function AvisoDeOrigem({
  afetados,
  onAbrir,
}: {
  afetados: readonly Redirector[];
  onAbrir: (redirector: Redirector) => void;
}) {
  return (
    <section
      className="mb-4 flex flex-col gap-3 rounded-lg bg-surface p-4 ring-1 ring-inset ring-warning/40 md:p-5"
      aria-label="Aviso de instalação"
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-md font-medium text-warning">
          {afetados.length === 1
            ? 'Um link está recebendo clique sem origem'
            : `${afetados.length} links estão recebendo clique sem origem`}
        </h2>
        <p className="text-sm text-muted-foreground">
          Esses cliques funcionam — a pessoa entra no grupo —, mas não dá para dizer de qual anúncio
          ela veio. Quase sempre é o botão da sua página mandando o link sem os parâmetros, e não o
          anúncio deixando de converter.
        </p>
      </div>

      <ul className="flex flex-col divide-y divide-border">
        {afetados.map((redirector) => (
          <li
            key={redirector.id}
            className="flex flex-col gap-2 py-2 md:flex-row md:items-center md:justify-between"
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium">{redirector.name}</span>
              <span className="font-mono text-2xs text-muted-foreground">
                {enderecoPublico(redirector) ?? `/r/${redirector.slug}`}
              </span>
              <OriginDiagnosisPanel redirector={redirector} compact />
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onAbrir(redirector)}
              className="max-md:w-full"
            >
              Ver como arrumar
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ========================================================================== */
/*  Linha e cartão                                                             */
/* ========================================================================== */

interface RowProps {
  redirector: Redirector;
  /** `null` = ainda não sabemos quais grupos este link alcança (invariante 8). */
  entrega: DeliveryReadiness | null;
  canUpdate: boolean;
  canDelete: boolean;
  onDetail: (redirector: Redirector) => void;
  onEdit: (redirector: Redirector) => void;
  onTogglePause: (redirector: Redirector) => void;
  onArchive: (redirector: Redirector) => void;
  onRemove: (redirector: Redirector) => void;
  onUnarchive: (redirector: Redirector) => void;
}

/**
 * O endereço, do jeito que se lê de uma tela: monoespaçado e sem parecer um campo.
 *
 * Recebe o redirecionador inteiro, e não só o `slug`, porque o endereço agora vem PRONTO do
 * servidor (`publicUrl`). Montá-lo aqui a partir do slug era a tela sabendo onde o produto
 * mora — uma segunda definição do mesmo endereço.
 */
function SlugText({ redirector }: { redirector: Redirector }) {
  return (
    <span
      className="block truncate font-mono text-2xs text-subtle-foreground"
      title="Endereço público do link. Fixo para sempre."
    >
      {enderecoPublico(redirector) ?? `/r/${redirector.slug}`}
    </span>
  );
}

/** O endereço, puro — para morar no `sub` do `TableEntity`, que já é mono. */
function enderecoDoLink(redirector: Redirector): string {
  return enderecoPublico(redirector) ?? `/r/${redirector.slug}`;
}

/**
 * A célula de cliques.
 *
 * `lastClickAt === null` é "nunca recebeu clique" e vira TEXTO, não `0` — porque `0` ali
 * diria "medi e deu zero" sobre um link que nunca foi medido (invariante 8).
 *
 * `variant` existe porque o número sozinho só se explica embaixo de um cabeçalho: na TABELA
 * a coluna já diz "Cliques", no CARTÃO do celular não há coluna nenhuma e um "312" solto não
 * quer dizer nada. E, no cartão, o estado "nunca clicado" já foi dito pelo selo de origem —
 * repeti-lo na linha de baixo é ruído.
 */
function ClicksCell({
  redirector,
  variant,
}: {
  redirector: Redirector;
  variant: 'table' | 'card';
}) {
  if (redirector.lastClickAt === null) {
    return variant === 'card' ? null : (
      <span className="text-2xs text-muted-foreground">ainda sem cliques</span>
    );
  }
  return (
    <span className={variant === 'table' ? 'flex flex-col items-end' : 'flex flex-col'}>
      <span className="text-sm">
        <span className="nums font-medium">{redirector.clicksTotal}</span>
        {variant === 'card' ? (
          <span className="text-muted-foreground">
            {redirector.clicksTotal === 1 ? ' clique' : ' cliques'}
          </span>
        ) : null}
      </span>
      <span className="text-2xs text-muted-foreground">
        último {formatRelative(redirector.lastClickAt)}
      </span>
    </span>
  );
}

/**
 * A campanha do link — e, embaixo dela, **o que esse vínculo entrega de verdade**.
 *
 * A campanha sozinha responde "quais grupos", que é meia resposta: o que decide o dia é
 * quantos daqueles grupos podem receber gente AGORA. Fica aqui, e não numa coluna nova,
 * porque é a mesma pergunta — e uma coluna a mais empurraria as ações para fora da tela no
 * notebook.
 *
 * Três formas diferentes para três estados diferentes (a cor não é canal único): ícone de
 * alerta quando nenhum grupo recebe, texto âmbar quando falta convite em algum, texto
 * discreto quando está tudo pronto. E quando ainda não sabemos, **não se escreve nada** —
 * um "0 de 0" enquanto a consulta está no ar seria mentira com cara de medição.
 */
function CampanhaCell({
  redirector,
  entrega,
}: {
  redirector: Redirector;
  entrega: DeliveryReadiness | null;
}) {
  const estrategia = REDIRECTOR_STRATEGY[redirector.strategy];
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm">{redirector.campaignName}</span>
      <span className="text-2xs text-muted-foreground" title={estrategia.consequence}>
        {estrategia.label}
        {redirector.pinnedGroupName === null ? '' : `: ${redirector.pinnedGroupName}`}
      </span>
      {entrega === null ? null : entrega.nenhumPronto ? (
        <span className="flex items-center gap-1 text-2xs text-warning">
          <AlertTriangle aria-hidden="true" className="size-3.5 shrink-0" />
          {resumoDaEntrega(entrega)}
        </span>
      ) : (
        <span
          className={
            entrega.semConvite > 0 ? 'text-2xs text-warning' : 'text-2xs text-muted-foreground'
          }
        >
          {resumoDaEntrega(entrega)}
        </span>
      )}
    </span>
  );
}

function OriginCell({ redirector }: { redirector: Redirector }) {
  const diagnosis = diagnoseOrigin(redirector);
  const spec = DIAGNOSIS[diagnosis.kind];
  return (
    <Badge tone={spec.tone} dot title={spec.summary(diagnosis)}>
      {spec.label}
    </Badge>
  );
}

function StateCell({ redirector }: { redirector: Redirector }) {
  const spec = redirectorState(redirector);
  return (
    <Badge tone={spec.tone} dot title={spec.hint}>
      {spec.label}
    </Badge>
  );
}

function RedirectorRow(props: RowProps) {
  const { redirector, entrega } = props;
  const archived = redirector.archivedAt !== null;

  return (
    <TableRow>
      <TableRowHeader>
        <span className="flex max-w-80 items-center gap-2.5">
          <Avatar name={redirector.name} shape="square" size="md" />
          <TableEntity
            title={redirector.name}
            sub={
              <span title="Endereço público do link. Fixo para sempre.">
                {enderecoDoLink(redirector)}
              </span>
            }
          />
        </span>
      </TableRowHeader>
      <TableCell>
        <CampanhaCell redirector={redirector} entrega={entrega} />
      </TableCell>
      <TableCell nowrap numeric>
        <ClicksCell redirector={redirector} variant="table" />
      </TableCell>
      <TableCell nowrap>
        <OriginCell redirector={redirector} />
      </TableCell>
      <TableCell nowrap>
        <StateCell redirector={redirector} />
      </TableCell>
      <TableCell sticky="right">
        <RowActions {...props} archived={archived} />
      </TableCell>
    </TableRow>
  );
}

function RedirectorCard(props: RowProps) {
  const { redirector, entrega } = props;
  const archived = redirector.archivedAt !== null;

  return (
    <div className="flex flex-col gap-2.5 px-4 py-3.5">
      <div className="flex items-start gap-2.5">
        <Avatar name={redirector.name} shape="square" size="md" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{redirector.name}</span>
          <SlugText redirector={redirector} />
        </div>
        <StateCell redirector={redirector} />
      </div>

      <span className="flex flex-wrap items-center gap-1">
        <OriginCell redirector={redirector} />
      </span>

      <CampanhaCell redirector={redirector} entrega={entrega} />

      <ClicksCell redirector={redirector} variant="card" />

      <RowActions {...props} archived={archived} />
    </div>
  );
}

/**
 * As ações da linha.
 *
 * Esconder por `can(...)` é UX, não segurança: o servidor barra de qualquer jeito. O que se
 * evita aqui é oferecer um botão que só daria erro.
 *
 * Reparar na ordem: **excluir só existe no link arquivado**, que é a mesma sequência que o
 * servidor exige — mostrá-lo no link ativo levaria a um "arquive antes" que a própria tela
 * poderia ter evitado.
 */
function RowActions({
  redirector,
  archived,
  canUpdate,
  canDelete,
  onDetail,
  onEdit,
  onTogglePause,
  onArchive,
  onRemove,
  onUnarchive,
}: RowProps & { archived: boolean }) {
  const nome = redirector.name;

  /*
   * Padrão do UI kit: a ação principal da linha fica visível ("Instalação" — é lá que estão o
   * link para copiar e o conserto da origem) e o resto mora no "…". Quatro botões de texto
   * lado a lado espremiam as colunas de dado até cortar o endereço.
   */
  const itens: ActionMenuItem[] = archived
    ? [
        ...(canUpdate
          ? [
              {
                id: 'desarquivar',
                label: 'Desarquivar',
                icon: <ArchiveRestore />,
                onSelect: () => onUnarchive(redirector),
              },
            ]
          : []),
        ...(canDelete
          ? [
              {
                id: 'excluir',
                label: 'Excluir',
                icon: <Trash2 />,
                destructive: true,
                separated: canUpdate,
                onSelect: () => onRemove(redirector),
              },
            ]
          : []),
      ]
    : canUpdate
      ? [
          { id: 'editar', label: 'Editar', icon: <Pencil />, onSelect: () => onEdit(redirector) },
          {
            id: 'pausar',
            label: redirector.isActive ? 'Pausar' : 'Retomar',
            icon: redirector.isActive ? <Pause /> : <Play />,
            onSelect: () => onTogglePause(redirector),
          },
          {
            id: 'arquivar',
            label: 'Arquivar',
            icon: <Archive />,
            separated: true,
            onSelect: () => onArchive(redirector),
          },
        ]
      : [];

  return (
    <span className="flex items-center justify-end gap-1">
      {!archived ? (
        <Button
          size="sm"
          leadingIcon={<MousePointerClick />}
          aria-label={`Instalação e cliques de ${nome}`}
          onClick={() => onDetail(redirector)}
        >
          Instalação
        </Button>
      ) : null}
      {itens.length > 0 ? <ActionMenu label={`Ações de ${nome}`} items={itens} /> : null}
    </span>
  );
}
